import { DOCUMENT, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  Inject,
  viewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { Icon, IconComponent } from '@examdojo/core/icon';
import {
  SelectOptionDirective,
  SelectOptionType,
  SelectTriggerDirective,
  SingleSelectComponent,
} from '@examdojo/core/select';
import { StopPropagationDirective } from '@examdojo/core/stop-propagation';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import { UnwrapArray } from '@examdojo/core/typescript';
import { TiptapNodeViewContentDirective } from 'ngx-tiptap';
import { v4 as uuid } from 'uuid';
import { CalloutBlock, CalloutColor } from '../../../model';
import { DragWithHandleOnlyDirective } from '../drag-with-handle-only.directive';
import { colorClassMap, ColorOption, colorToLabel } from './key-point-callout-color.config';
import { generateGutterBackgroundImage } from './key-point-callout-gutter.util';
import { DEFAULT_KEY_POINT_CALLOUT_PROPS } from './key-point-callout.model';

@Component({
  selector: 'dojo-key-point-callout',
  standalone: true,
  imports: [
    NgClass,
    IconComponent,
    TiptapNodeViewContentDirective,
    StopPropagationDirective,
    SingleSelectComponent,
    SelectOptionDirective,
    SelectTriggerDirective,
    MatMenuModule,
  ],
  templateUrl: './key-point-callout.component.html',
  styleUrls: ['./key-point-callout-shared.scss', './key-point-callout.component.scss'],
  hostDirectives: [DragWithHandleOnlyDirective],
  providers: [
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => KeyPointCalloutComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeyPointCalloutComponent extends NodeViewComponent<CalloutBlock> {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    super();

    // Update title content reactively, but only when not being edited
    effect(() => {
      const element = this.titleElement()?.nativeElement;
      if (element && this.document.activeElement !== element) {
        element.textContent = this.title();
      }
    });

    // Update color control when color attribute changes
    effect(() => {
      const currentColor = this.color();
      if (this.colorCtrl.value !== currentColor) {
        this.colorCtrl.setValue(currentColor);
      }
    });
  }

  private readonly titleElement = viewChild<ElementRef<HTMLSpanElement>>('titleElement');

  readonly colorOptions: ColorOption[] = Object.values(CalloutColor).map((color) => ({
    label: colorToLabel[color],
    value: color,
    colorClass: colorClassMap[color].bg,
  }));

  readonly selectOptionType!: SelectOptionType<UnwrapArray<typeof this.colorOptions>>;

  readonly colorCtrl = new FormControl<CalloutColor>(CalloutColor.Yellow, { nonNullable: true });

  readonly title = this.getProp('title');
  readonly color = this.getProp('color');
  readonly icon = this.getProp('icon');
  readonly customCss = this.getProp('custom_css');

  readonly isEditable = computed(() => this.editor().isEditable);

  readonly colorClasses = computed(() => {
    const color = this.color();
    const classes = colorClassMap[color];
    return {
      border: classes.border,
      text: classes.text,
      gutter: classes.gutter,
      gutterStrokeCssVar: classes.gutterStrokeCssVar,
    };
  });

  readonly gutterBackgroundImage = computed((): string => {
    const cssVarName = this.colorClasses().gutterStrokeCssVar;
    const computedColor = getComputedStyle(this.document.documentElement).getPropertyValue(cssVarName).trim();
    return generateGutterBackgroundImage(computedColor || '#9c9c9c');
  });

  readonly availableIcons: Icon[] = [
    'key',
    'lightbulb',
    'star',
    'flag',
    'bookmark',
    'exclamation-circle',
    'info-circle',
    'check-circle',
    'question-circle',
    'exclamation-triangle',
    'fire',
    'heart',
    'gem',
    'trophy',
    'bell',
    'bolt',
    'rocket',
    'shield',
    'puzzle-piece',
    'wand-magic-sparkles',
    'circle-check',
    'circle-info',
    'circle-exclamation',
    'circle-question',
    'book',
    'graduation-cap',
    'award',
    'medal',
  ];

  override getDefaults = (): CalloutBlock => ({
    ...DEFAULT_KEY_POINT_CALLOUT_PROPS,
    id: uuid(),
  });

  updateTitle(event: Event): void {
    const target = event.target as HTMLElement;
    const newTitle = target.textContent || '';
    this.updateProp('title', newTitle);
  }
}
